import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [heroScrolled, setHeroScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isHome = location.pathname === '/';

  useEffect(() => {
    // Reset heroScrolled when navigating to home so intro replays on full reload.
    // (State persists within a session — that's intentional for SPA nav.)
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);

    const onFirstScroll = () => setHeroScrolled(true);
    window.addEventListener('scroll', onFirstScroll, { once: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', onFirstScroll);
    };
  }, []);

  // Browser back/forward changes the route without going through a link's
  // onClick, which would otherwise leave the drawer hanging open.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // On non-home pages the header is always visible
  const headerHidden = isHome && !heroScrolled;
  // When hero is in view (home, transparent, after first scroll)
  const overDarkHero = isHome && !scrolled;

  const handleSignInClick = () => navigate('/login');

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
      headerHidden
        ? 'opacity-0 -translate-y-full pointer-events-none'
        : scrolled
          ? 'opacity-100 translate-y-0 bg-white/95 backdrop-blur-xl border-b border-slate-100 shadow-sm'
          : 'opacity-100 translate-y-0 bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <h1 className={`text-2xl font-black transition-colors duration-300 ${scrolled ? 'text-slate-800' : overDarkHero ? 'text-white' : 'text-slate-800'}`}>
              Karvlo
            </h1>
            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wider ${
              scrolled ? 'bg-teal-50 text-teal-600' : overDarkHero ? 'bg-white/15 text-white/70 border border-white/25' : 'bg-slate-100 text-slate-500 border border-slate-200'
            }`}>
              BETA
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {[
              { to: '/', label: 'Home' },
              { to: '/features', label: 'Features' },
              { to: '/about', label: 'About' },
              { to: '/faq', label: 'FAQ' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-semibold transition-colors duration-300 ${
                  scrolled ? 'text-slate-500 hover:text-slate-800' : overDarkHero ? 'text-white/75 hover:text-white' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="/#scene-signup"
              className={`text-sm font-semibold transition-colors duration-300 ${
                scrolled ? 'text-slate-500 hover:text-slate-800' : overDarkHero ? 'text-white/75 hover:text-white' : 'text-slate-500 hover:text-slate-800'
              }`}
              onClick={(e) => {
                if (!isHome) {
                  e.preventDefault();
                  window.location.href = '/#scene-signup';
                }
              }}
            >
              Join Beta
            </a>
            <button
              onClick={handleSignInClick}
              className={`px-5 py-2 rounded-xl text-sm font-bold transition-all duration-300 ${
                scrolled
                  ? 'bg-slate-800 text-white hover:bg-slate-700'
                  : overDarkHero
                    ? 'bg-white/15 text-white border border-white/30 hover:bg-white/25 backdrop-blur-sm'
                    : 'bg-slate-800 text-white hover:bg-slate-700 shadow-sm'
              }`}
            >
              Sign In
            </button>
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-nav"
          >
            <div className="w-6 flex flex-col gap-1.5">
              <span className={`block h-0.5 rounded-full transition-all duration-300 ${scrolled ? 'bg-slate-800' : overDarkHero ? 'bg-white' : 'bg-slate-700'} ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block h-0.5 rounded-full transition-all duration-300 ${scrolled ? 'bg-slate-800' : overDarkHero ? 'bg-white' : 'bg-slate-700'} ${mobileMenuOpen ? 'opacity-0' : ''}`} />
              <span className={`block h-0.5 rounded-full transition-all duration-300 ${scrolled ? 'bg-slate-800' : overDarkHero ? 'bg-white' : 'bg-slate-700'} ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </div>
          </button>
        </div>

        {/* Mobile menu */}
        <div
          id="mobile-nav"
          className={`md:hidden absolute left-0 right-0 z-40 transition-all duration-300 ${
            mobileMenuOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          style={{ top: '100%' }}
        >
          <div className="bg-white/98 backdrop-blur-xl border-b border-slate-100 shadow-lg px-6 py-8">
            <nav className="flex flex-col gap-4">
              <Link to="/" className="text-lg text-slate-600 hover:text-slate-800 font-semibold py-2" onClick={() => setMobileMenuOpen(false)}>
                Home
              </Link>
              <Link to="/features" className="text-lg text-slate-600 hover:text-slate-800 font-semibold py-2" onClick={() => setMobileMenuOpen(false)}>
                Features
              </Link>
              <Link to="/about" className="text-lg text-slate-600 hover:text-slate-800 font-semibold py-2" onClick={() => setMobileMenuOpen(false)}>
                About
              </Link>
              <Link to="/faq" className="text-lg text-slate-600 hover:text-slate-800 font-semibold py-2" onClick={() => setMobileMenuOpen(false)}>
                FAQ
              </Link>
              <a href="/#scene-signup" className="text-lg text-slate-600 hover:text-slate-800 font-semibold py-2" onClick={() => { setMobileMenuOpen(false); if (!isHome) window.location.href = '/#scene-signup'; }}>
                Join Beta
              </a>
              <button
                onClick={() => { setMobileMenuOpen(false); handleSignInClick(); }}
                className="mt-2 w-full py-3 bg-slate-800 text-white font-bold rounded-xl shadow-lg hover:bg-slate-700 transition-all"
              >
                Sign In
              </button>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
